<template>
  <page-header>
    <template v-slot:title>
      <nav class="flex pb-8" aria-label="Breadcrumb" v-if="!showPrescriberDetails">
        <ol role="list" class="flex items-center space-x-2">
          <li>
            <div>
              <router-link to="/">
                <a class="text-white hover:text-gray-500">
                  <HomeIcon class="h-5 w-5 flex-shrink-0 content-center" aria-hidden="true" />
                  <span class="sr-only">Home</span>
                </a>
              </router-link>
            </div>
          </li>
          <li v-for="(breadCrumb, index) in breadCrumbDetails" :key="index">
            <div class="flex items-center">
              <ChevronRightIcon class="h-5 w-5 flex-shrink-0 content-center text-gray-400" aria-hidden="true" />
              <a
                :class="breadCrumb.cursor ? ' cursor-pointer border-b border-white' : 'cursor-text '"
                class="ml-2 text-sm font-medium text-white hover:text-gray-700"
                aria-current="page"
                @click.prevent="setUpBreadCrumbs(breadCrumb)"
              >
                {{ breadCrumb.title }}
              </a>
            </div>
          </li>
        </ol>
      </nav>
      <div
        v-if="showPrescriberDetails || personalDetailsEdit || showChangePassword || showPrescriberTreatmentAreaDetails"
      >
        Account
      </div>
      <div class="flex item-start pt-2 flex-col md:flex-row space-y-2" v-if="organisationDetailsEdit">
        <div class="flex-1">
          <div class="text-2xl font-semibold leading-6 flex-1">Organisaties & afdelingen</div>
        </div>
        <div class="flex-2 grid-align-end self-end">
          <v-button tagelement="a" md primary cta icon-left>
            <v-plus-circle-icon title="" />
            Organisatie toevoegen
          </v-button>
        </div>
      </div>
      <div v-if="organisationDepartmentDetailsEdit">{{ currentOrgarnisationName }}</div>
      <div class="flex item-start pt-2" v-if="departmentProxyDetails">
        <div class="flex-1">
          <div class="text-2xl font-semibold leading-6 flex-1">{{ currentDepartment.name }}</div>
        </div>
        <div class="flex-2 grid-align-end" v-if="showProxy">
          <v-button tagelement="a" md primary cta icon-left @click.prevent="(showProxy = false), (addProxy = true)">
            <v-plus-circle-icon title="" />
            Volmachtgever toevoegen
          </v-button>
        </div>
      </div>
    </template>
  </page-header>
  <div class="prescriberDetailsEdit -mt-32">
    <div class="mx-auto w-10/12 pb-12">
      <div class="rounded-lg py-6" v-if="showPrescriberDetails">
        <div class="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-2" v-if="userDetails.externalId">
          <card>
            <template v-slot>
              <div class="flex flex-col md:flex-row item-start">
                <div class="flex flex-1">
                  <div class="flex-shrink-0 content-center"><v-account-outline></v-account-outline></div>
                  <div class="flex-1">
                    <div class="text-2xl font-semibold leading-6 flex-1 mx-2">Persoonsgegevens</div>
                  </div>
                </div>
                <div class="flex-2 justify-items-end">
                  <div class="text-large leading-6 flex-1 mx-2 my-1 cursor-pointer" @click="editPersonalDetails">
                    Wijzigen
                  </div>
                </div>
              </div>
              <div class="grid grid-rows-6 my-2 text-black">
                <div class="py-1">
                  <span v-if="userDetails.gender === 1">De heer </span>
                  <span v-if="userDetails.gender === 2">Mevrouw </span>
                  <span v-if="userDetails.firstName">{{ userDetails.firstName }} </span>
                  ({{ userDetails.initials }})
                  <span v-if="userDetails.prePosition">{{ userDetails.prePosition }} </span>
                  {{ userDetails.lastName }}
                </div>
                <div class="py-1" v-if="userDetails.mobileNumber">
                  <span v-if="userDetails.phoneNumberDialCode">+{{ userDetails.phoneNumberDialCode + ' ' }} </span>
                  <span v-if="userDetails.mobileNumber"> {{ userDetails.mobileNumber }}</span>
                </div>
                <div class="py-1">
                  {{ userDetails.email }}
                </div>
                <div class="pt-2 pb-1">AGB-code: {{ userDetails.agbNumber }}</div>
                <div class="py-1">BIG-nummer: {{ userDetails.bigNumber }}</div>
              </div>
            </template>
          </card>
          <card>
            <template v-slot>
              <div class="flex flex-col md:flex-row item-start">
                <div class="flex flex-1">
                  <div class="flex-shrink-0 content-center"><v-hand-heart-outline></v-hand-heart-outline></div>
                  <div class="flex-1">
                    <div class="text-2xl font-semibold leading-6 flex-1 mx-2">Behandelgebieden</div>
                  </div>
                </div>
                <div class="flex-2 justify-items-end">
                  <div
                    class="text-large leading-6 flex-1 mx-2 my-1 cursor-pointer"
                    @click.prevent="editTreatmentAreaDetails"
                  >
                    Wijzigen
                  </div>
                </div>
              </div>
              <div class="grid grid-rows my-2 max-h-56 overflow-y-auto">
                <div
                  class="border-b border-indigo-200 border-opacity-25 text-black py-2"
                  v-for="(treatmentArea, index) in userDetails.treatmentAreas"
                  :key="index"
                >
                  {{ treatmentArea.treatmentArea }}
                </div>
              </div>
            </template>
          </card>
        </div>
        <div class="grid py-4">
          <card>
            <template v-slot>
              <div class="flex item-start">
                <div class="flex-shrink-0 content-center"><v-home-city-outline></v-home-city-outline></div>
                <div class="flex-1">
                  <div class="text-2xl font-semibold leading-6 flex-1 mx-2">Organisaties en afdelingen</div>
                </div>
              </div>
              <div class="grid grid-rows my-2 lowercase">
                <div
                  class="py-2 capitalize underline font-bold cursor-pointer"
                  @click.prevent="editOrganisationDetails"
                >
                  Alle organisaties en afdelingen
                </div>
                <div
                  class="py-2 capitalize underline cursor-pointer"
                  v-for="(institution, index) in userDetails.institutions"
                  :key="index"
                  @click.prevent="editOrganisationDepartments(institution.institutionId, institution.name)"
                >
                  {{ institution.name }}
                </div>
              </div>
            </template>
          </card>
        </div>
        <div class="grid py-4">
          <card>
            <template v-slot>
              <div class="flex flex-col md:flex-row item-start">
                <div class="flex flex-1">
                  <div class="flex-shrink-0 content-center"><Email-icon></Email-icon></div>
                  <div class="flex-1 content-center">
                    <div class="text-2xl font-semibold leading-6 flex-1 mx-2">E-mail notificaties</div>
                  </div>
                </div>
                <div class="flex-2 justify-items-end">
                  <div
                    class="text-large leading-6 flex-1 mx-2 my-1 cursor-pointer"
                    v-if="disableEmailSelection"
                    @click.prevent="editUserPreferenceEmail(true)"
                  >
                    Wijzigen
                  </div>
                  <button
                    v-if="!disableEmailSelection && setNotificationEmails"
                    type="button"
                    class="text-center rounded-full border w-32 h-10 text-white bg-pdsPrimaryColor cursor-pointer border-pdsPrimaryColor"
                    @click.prevent="saveUserPreferenceEmail"
                  >
                    Opslaan
                  </button>
                </div>
              </div>
              <div class="grid grid-rows my-2">
                <div class="relative flex items-start py-2">
                  <div class="flex h-5 items-center">
                    <input
                      id="notificationOn"
                      aria-describedby="notificationOn"
                      name="notificationOn"
                      type="checkbox"
                      v-model="setNotificationEmails"
                      class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                    />
                  </div>
                  <div class="ml-3">
                    <label for="notificationOn" class="text-black"> E-mail notificaties ontvangen </label>
                  </div>
                </div>
                <Alert
                  v-if="!disableEmailSelection"
                  :html="true"
                  htmlMessage="Geef hieronder in per afdeling op welk email adres u graag notifictions zou willen ontvangen"
                  type="info"
                ></Alert>
              </div>
              <div v-if="setNotificationEmails" class="grid lowercase">
                <div
                  class="flex md:grid flex-col md:grid-cols-2 md:flex-row items-start md:items-center border-b divide-balternative-color-200 md:border-none"
                  v-for="(department, index) in availableDepartments"
                  :key="index"
                >
                  <span class="capitalize min-w-40 pt-2"> {{ department.name }}</span>
                  <div class="w-full md:w-1/2 pt-2">
                    <combo-boxes
                      v-if="!disableEmailSelection"
                      customClass="pr-14 outline outline-1 focus:outline-2"
                      :list="department.emails"
                      :disabled="disableEmailSelection"
                      :disabledInput="'true'"
                      v-model="department.selected"
                    >
                    </combo-boxes>
                    <span clas="pt-2" v-else>{{ department.selected.data }}</span>
                  </div>
                </div>
              </div>
            </template>
          </card>
        </div>
        <div class="grid py-4">
          <card>
            <template v-slot>
              <div class="flex item-start">
                <div class="flex-shrink-0 content-center"><v-shield-check-outline></v-shield-check-outline></div>
                <div class="flex-1">
                  <div class="text-2xl font-semibold leading-6 flex-1 mx-2">Beveiliging</div>
                </div>
              </div>
              <div class="grid grid-rows my-2">
                <div class="py-2 underline cursor-pointer" @click.prevent="setChangePassword">Wachtwoord wijzigen</div>
                <div v-if="show2FA" class="py-2 underline cursor-pointer" @click.prevent="setTwoFactorAuthentication">
                  2FA instellen
                </div>
              </div>
            </template>
          </card>
        </div>
      </div>
      <div class="rounded-lg px-8 py-6" v-else>
        <div v-if="personalDetailsEdit">
          <card>
            <template v-slot>
              <div class="flex item-start">
                <div class="flex-shrink-0 content-center"><v-pen></v-pen></div>
                <div class="flex-1 w-full min-w-0 break-words whitespace-normal">
                  <div class="text-2xl font-semibold leading-6 flex-1 mx-2">Persoonsgegevens wijzigen</div>
                </div>
              </div>
              <div class="flex flex-col my-2">
                <div>
                  <p class="text-black font-bold m-0 px-2 py-2">Aanhef *</p>
                  <div class="flex px-2">
                    <input
                      id="genderMale"
                      name="prescriberGender"
                      type="radio"
                      :checked="editedUserPersonalDetails.gender === 1"
                      class="h-8 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      @change="checkRadioAddress(1)"
                    />
                    <label
                      for="genderMale"
                      class="ml-3 block text-sm font-medium leading-6 text-gray-900 content-center"
                    >
                      Dhr.
                    </label>
                  </div>
                  <div class="flex px-2">
                    <input
                      id="genderFemale"
                      name="prescriberGender"
                      type="radio"
                      class="h-8 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                      :checked="editedUserPersonalDetails.gender === 2"
                      @change="checkRadioAddress(2)"
                    />
                    <label
                      for="genderFemale"
                      class="ml-3 block text-sm font-medium leading-6 text-gray-900 content-center"
                    >
                      Mevr.
                    </label>
                  </div>
                  <div v-if="!editedUserPersonalDetails.gender" class="form-group-simple__details px-2">
                    <div class="form-group-simple__messages">
                      <div class="form-group-simple__error">* Aanhef is verplicht.</div>
                    </div>
                  </div>
                </div>
                <div class="mt-2">
                  <p class="text-black font-bold px-2 pt-2 mb-3.5">Telefoonnummer*</p>
                  <div class="flex gap-2 w-fit ml-2">
                    <div class="w-64">
                      <vue3-country-intl
                        noDataText=""
                        v-model="editedUserPersonalDetails.phoneNumberDialCode"
                        placeholder=""
                        :only-value="true"
                        @change="onTelephoneNumberSelect('phoneNumberDialCode', $event)"
                      />
                    </div>
                    <div class="w-64">
                      <v-input-group-simple-2
                        id="mobileNumber"
                        :value="getComputedPhoneNumber(editedUserPersonalDetails.mobileNumber)"
                        :rules="{
                          required: true,
                          regex: /^(?:\d{1,11}|(?=\d+ \d+$)[\d ]{3,12})$/,
                          numeric: true,
                          max: 999999999,
                          min: 100000000
                        }"
                        @input="updatePersonalDetails('mobileNumber', $event)"
                      />
                    </div>
                  </div>
                </div>
                <div class="mt-2">
                  <div class="flex items-center px-2 py-2">
                    <div class="w-full md:w-1/4">
                      <v-input-group-simple-2
                        id="email"
                        v-model:value="editedUserPersonalDetails.email"
                        type="text"
                        :rules="{
                          required: true,
                          email: true,
                          regex: /@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/
                        }"
                        label="E-mailadres"
                        @input="updatePersonalDetails('email', $event)"
                      />
                      <div v-if="showUserEmailExistAlert" class="form-group-simple__details">
                        <div class="form-group-simple__messages">
                          <div class="form-group-simple__error">* E-mailadres is al bekend.</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="px-2 pt-2">
                  <p>
                    <span class="text-black">
                      In het geval dat u gegevens wilt wijzigen waarvoor dit formulier geen mogelijkheid biedt, vragen
                      wij u contact met ons op te nemen via dit
                    </span>
                    <span class="underline cursor-pointer" @click="manualEditingForm = true">formulier.</span>
                  </p>
                </div>
              </div>
              <div class="flex flex-col md:flex-row justify-end">
                <v-button md cta hollow transparent @click="cancelPersonalDetailsSave"> Annuleren </v-button>
                <v-button md cta @click.prevent="savePersonalDetails"> Wijzigen </v-button>
              </div>
            </template>
          </card>
        </div>
        <div v-if="organisationDetailsEdit">
          <div class="flex py-4" v-for="(institution, index) in userInstitutions" :key="index">
            <card class="w-full min-w-0 break-words whitespace-normal">
              <template v-slot>
                <div class="flex item-start">
                  <div class="flex-shrink-0 content-center"><v-home-city-outline></v-home-city-outline></div>
                  <div class="flex-1">
                    <div class="text-2xl font-semibold leading-6 flex-1 mx-2">{{ institution.name }}</div>
                  </div>
                </div>
                <div class="flex flex-col my-2 text-black">
                  <div class="py-1">
                    {{ institution.institutionAddress.streetName }} {{ institution.institutionAddress.houseNumber }}
                  </div>
                  <div class="py-1">
                    {{ institution.institutionAddress.postalCode }} {{ institution.institutionAddress.city }}
                  </div>
                  <div class="py-1">AGB-code: {{ institution.agbCode }}</div>
                  <div class="py-1" v-if="institution.email">{{ institution.email }}</div>
                </div>
                <div class="flex flex-col mt-4 my-2 lowercase">
                  <div
                    v-if="institution.departments.length > 1"
                    class="py-1 capitalize underline font-bold cursor-pointer"
                    @click.prevent="editOrganisationDepartments(institution.institutionId, institution.name)"
                  >
                    Alle afdelingen
                  </div>
                  <div
                    class="py-1 capitalize underline cursor-pointer"
                    v-for="(dep, index) in institution.departments"
                    :key="index"
                    @click.prevent="
                      editOrganisationDepartments(institution.institutionId, institution.name, dep.departmentId)
                    "
                  >
                    {{ dep.name }}
                  </div>
                </div>
              </template>
            </card>
          </div>
        </div>
        <div v-if="organisationDepartmentDetailsEdit">
          <div class="grid py-4" v-for="(department, index) in userInstitutionDepartments" :key="index">
            <card>
              <template v-slot>
                <div class="flex flex-col md:flex-row item-start" :id="department.departmentId">
                  <div class="flex flex-1">
                    <div class="flex-shrink-0 content-center"><v-account-group-outline></v-account-group-outline></div>
                    <div class="flex-1">
                      <div class="text-2xl font-semibold leading-6 flex-1 mx-2">{{ department.name }}</div>
                    </div>
                  </div>
                  <div class="flex-2 justify-items-end">
                    <div class="text-large leading-6 flex-1 mx-2 my-1 cursor-pointer">Wijzigen</div>
                  </div>
                </div>
                <div class="grid grid-rows my-2 text-black">
                  <div class="py-1">
                    {{ department.departmentAddress.streetName }} {{ department.departmentAddress.houseNumber }}
                  </div>
                  <div class="py-1">
                    {{ department.departmentAddress.postalCode }} {{ department.departmentAddress.city }}
                  </div>
                  <div class="py-1" v-if="department.email">{{ department.email }}</div>
                </div>
                <div class="grid grid-rows mt-4 my-2 lowercase">
                  <div
                    class="py-1 capitalize underline font-bold cursor-pointer"
                    @click.prevent="showDepartmentProxyDetails(department)"
                  >
                    Naar volmachtgevers
                  </div>
                </div>
              </template>
            </card>
          </div>
        </div>
        <div v-if="departmentProxyDetails">
          <v-prescriber-proxy-user-details
            :addProxyUserStatus="addProxy"
            @set-department-bread-crumb="setDepartmentBreadCrumb"
            @go-back-to-department="goBackToDepartment"
            @show-department-proxy-details="showDepartmentProxyDetails(currentDepartment)"
            :proxy-user-details="proxyUserDetails"
            :userDetails="userDetails"
            :department="currentDepartment"
          ></v-prescriber-proxy-user-details>
        </div>
        <div v-if="showChangePassword">
          <card>
            <template v-slot>
              <div class="grid my-2 justify-self-center">
                <create-first-password
                  @success="(showChangePassword = false), (showPrescriberDetails = true)"
                ></create-first-password>
              </div>
            </template>
          </card>
        </div>
        <div v-if="showTwoFactorAuthentication">
          <card v-if="!editTwoFactorAuthentication">
            <template v-slot>
              <div class="flex item-start">
                <div class="flex-shrink-0 content-center"><v-shield-check-outline></v-shield-check-outline></div>
                <div class="flex-1">
                  <div class="text-2xl font-semibold leading-6 flex-1 mx-2">2FA instellen</div>
                </div>
              </div>
              <div class="grid my-2">
                <div>
                  <p>
                    Met het instellen van tweefactorauthenticatie verhoogt u de beveiliging van uw account. Naast uw
                    wachtwoord, zal het systeem u vragen om een 2de verificatie methode middels een automatisch
                    gegenereerde code. U gebruikt hiervoor een app op uw mobiele telefoon of een klein apparaatje dat de
                    codes voor u genereert.
                  </p>
                  <a class="underline" href="/service#Question">
                    Lees meer
                    <ArrowUpRightIcon
                      class="h-5 w-5 flex-shrink-0 content-center inline"
                      aria-hidden="true"
                    ></ArrowUpRightIcon>
                  </a>
                  <div class="mt-4">
                    2FA inschakelen
                    <toggle
                      class="align-middle ml-2"
                      @enable-status="TwoFactorAuthToggle"
                      :initialValue="currentUserIdentity.twoFactorAuthentication.enabled"
                      :overwrite-value="currentUserIdentity.twoFactorAuthentication.enabled"
                    ></toggle>
                  </div>
                </div>
              </div>
              <div class="flex justify-end">
                <v-button
                  md
                  cta
                  hollow
                  transparent
                  @click="
                    (showTwoFactorAuthentication = false),
                      (showPrescriberDetails = true),
                      (enableTwoFactorAuthencation = false)
                  "
                >
                  Annuleren
                </v-button>
              </div>
            </template>
          </card>
          <div v-else>
            <prescriber-two-factor-enable-auth
              v-if="enableTwoFactorAuthencation"
              @close-edit-authentication="(editTwoFactorAuthentication = false), (showTwoFactorAuthentication = true)"
              @success="successNotification('2FA succesvol ingeschakeld. Vanaf nu moet een code worden gebruikt.')"
              @error="errorNotification('Er is een fout opgetreden, probeer het later nog eens')"
            />
            <prescriber-two-factor-disable-auth
              v-else
              @close-edit-authentication="(editTwoFactorAuthentication = false), (showTwoFactorAuthentication = true)"
              @success="successNotification('Succesvol uitgeschakeld. Code is niet meer nodig.')"
              @error="errorNotification('Er is een fout opgetreden, probeer het later nog eens')"
            />
          </div>
        </div>
        <div v-if="showPrescriberTreatmentAreaDetails">
          <prescriber-treatment-area-details
            :treatmentAreaDetails="userDetails.treatmentAreas"
            @cancel="(showPrescriberDetails = true), (showPrescriberTreatmentAreaDetails = false)"
            @save="savePrescriberTreatmentAreaDetails"
          ></prescriber-treatment-area-details>
        </div>
      </div>
    </div>
  </div>
  <generic-modal v-if="manualEditingForm">
    <template #body>
      <v-heading :level="3"> Uw bericht: </v-heading>
      <div class="row grid-margin">
        <div class="col-xs-12">
          <v-input-group-simple-2
            id="Onderwerp"
            v-model:value="manualEditingFormDetails.subject"
            :rules="{
              required: true
            }"
            type="text"
            label="Onderwerp"
          />
        </div>
      </div>
      <div class="row grid-margin">
        <div class="col-xs-12">
          <v-textarea-group
            v-model="manualEditingFormDetails.body"
            label="Uw bericht *"
            inputid="Uw bericht"
            @change="saveEditDetailsInput"
          />
          <div v-show="showPopupErrorMessage" class="form-group-simple__details">
            <div class="form-group-simple__messages">
              <div class="form-group-simple__error">* Dit veld is verplicht.</div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <v-button md hollow transparent @click.prevent="closeModal"> Annuleren </v-button>
      <v-button md primary @click.prevent="sendManualDetails"> Versturen </v-button>
    </template>
  </generic-modal>
  <generic-modal v-if="showSuccessMessage" :has-close-button="true" @close="closeSuccessModal">
    <template #header> Bericht verstuurd! </template>
    <template #body>
      <div>
        <p>
          Het gegevensformulier is verstuurd en wordt spoedig in behandeling genomen. Voor meer informatie kijk ook op
          onze
          <router-link to="/service"> <span @click="closeSuccessModal">Veelgestelde vragen</span> </router-link>.
        </p>
      </div>
    </template>
    <template #footer>
      <v-button md primary @click="closeSuccessModal"> Ok </v-button>
    </template>
  </generic-modal>
</template>

<script>
import { ref, onBeforeMount, getCurrentInstance, computed, watch } from 'vue';
import VAccountOutline from 'icons/AccountOutline';
import VAccountGroupOutline from 'icons/AccountGroupOutline';
import VHandHeartOutline from 'icons/HandHeartOutline';
import VHomeCityOutline from 'icons/HomeCityOutline';
import VShieldCheckOutline from 'icons/ShieldCheckOutline';
import VPen from 'icons/Pen';
import { mapActions } from 'vuex';
import { currentUserIdentity$ } from '@/services';
import { ArrowUpRightIcon, ChevronRightIcon, HomeIcon } from '@heroicons/vue/20/solid';
import EmailIcon from 'icons/Email';
import axios from 'axios';
import GenericModal from '@/components/shared/GenericModal';
import { useForm } from 'vee-validate';
import VPlusCircleIcon from 'icons/PlusCircle';
import VPrescriberProxyUserDetails from './edit/PrescriberProxyUserDetails.vue';
import CreateFirstPassword from '@/components/sections/login/CreateFirstPassword';
import PrescriberTwoFactorEnableAuth from './edit/PrescriberTwoFactorEnableAuth';
import PrescriberTwoFactorDisableAuth from './edit/PrescriberTwoFactorDisableAuth';
import PrescriberTreatmentAreaDetails from './edit/PrescriberTreatmentAreaDetails.vue';

export default {
  name: 'PrescriberProfileDetails',
  components: {
    GenericModal,
    VAccountOutline,
    VHandHeartOutline,
    VHomeCityOutline,
    VShieldCheckOutline,
    ChevronRightIcon,
    HomeIcon,
    EmailIcon,
    VPen,
    VPlusCircleIcon,
    VAccountGroupOutline,
    VPrescriberProxyUserDetails,
    CreateFirstPassword,
    PrescriberTwoFactorEnableAuth,
    PrescriberTwoFactorDisableAuth,
    ArrowUpRightIcon,
    PrescriberTreatmentAreaDetails
  },
  setup() {
    const userDetails = ref({});
    const editedUserPersonalDetails = ref({});
    const userInstitutions = ref([]);
    const userInstitutionDepartments = ref([]);
    const { validate } = useForm();
    const $emitter = getCurrentInstance().appContext.config.globalProperties.$emitter;
    const currentUserIdentity = ref(null);
    const showPrescriberDetails = ref(true);
    const personalDetailsEdit = ref(false);
    const organisationDetailsEdit = ref(false);
    const organisationDepartmentDetailsEdit = ref(false);
    const departmentProxyDetails = ref(false);
    const showProxy = ref(false);
    const proxyUserDetails = ref([]);
    const addProxy = ref(false);
    const manualEditingForm = ref(false);
    const disableEmailSelection = ref(true);
    const manualEditingFormDetails = ref({
      subject: 'Aanvraag gegevens wijziging voorschrijver',
      body: ''
    });
    const show2FA = ref(false);
    const showPopupErrorMessage = ref(false);
    const showSuccessMessage = ref(false);
    const showUserEmailExistAlert = ref(false);
    const emailChanged = ref(false);
    let isInitialRun = true;
    const breadCrumbDetails = ref([]);
    const currentOrgarnisationName = ref(null);
    const currentDepartment = ref({});
    const showChangePassword = ref(false);
    const showTwoFactorAuthentication = ref(false);
    const editTwoFactorAuthentication = ref(false);
    const enableTwoFactorAuthencation = ref(false);
    const showPrescriberTreatmentAreaDetails = ref(false);
    const availableDepartments = ref(null);
    const ppwRecId = ref(null);
    let initialLoad = true;
    const setNotificationEmails = ref(null);

    currentUserIdentity$.subscribe((value) => (currentUserIdentity.value = value));

    // Fetch the data when the component is mounted
    onBeforeMount(() => {
      getUserDetails();
      getUserEmails();
      getUserCommunicationPreference();
    });

    const setEditUserDetails = () => {
      editedUserPersonalDetails.value.gender = userDetails.value.gender;
      editedUserPersonalDetails.value.email = userDetails.value.email;
      editedUserPersonalDetails.value.mobileNumber = userDetails.value.mobileNumber
        ? userDetails.value.mobileNumber
        : null;
      editedUserPersonalDetails.value.phoneNumberDialCode = userDetails.value.phoneNumberDialCode
        ? userDetails.value.phoneNumberDialCode
        : null;
    };

    const getUserDetails = () => {
      axios.get('UserV2/GetUser').then((resposne) => {
        userDetails.value = resposne && resposne.data ? resposne.data : {};
        setEditUserDetails();
      });
    };

    const getUserEmails = () => {
      axios
        .get('UserV2/GetUserPreferenceEmails?userExternalId=' + currentUserIdentity.value.externalId)
        .then((response) => {
          let departments = response && response.data ? response.data : {};
          availableDepartments.value = departments.map((dept) => {
            const emailArray = [];
            Object.keys(dept.emails).forEach((key) => {
              if (dept.emails[key]) {
                emailArray.push({
                  value: key,
                  data: dept.emails[key]
                });
              }
            });
            return {
              ppwrecId: dept.ppwrecId,
              name: dept.name,
              emails: emailArray,
              selected: emailArray.filter((x) => x.data == dept.activeEmail)[0]
                ? emailArray.filter((x) => x.data == dept.activeEmail)[0]
                : emailArray.filter((x) => x.value == 'userEmail')[0]
            };
          });
        });
    };
    const getUserCommunicationPreference = () => {
      axios.get('UserV2/GetUserCommunicationPreference?userId=' + currentUserIdentity.value.id).then((response) => {
        if (response.data && response.data == true) {
          setNotificationEmails.value = true;
        } else {
          setNotificationEmails.value = false;
        }
      });
    };
    const saveEditDetailsInput = (event) => {
      manualEditingFormDetails.value.body = event.target.value;
      if (event.target.value) {
        showPopupErrorMessage.value = false;
      } else {
        showPopupErrorMessage.value = true;
      }
    };

    const sendManualDetails = () => {
      if (!manualEditingFormDetails.value.body) {
        showPopupErrorMessage.value = true;
        return;
      }
      axios
        .post('Registration/EmailUserDataChangeRequestToMdm', { content: manualEditingFormDetails.value.body })
        .then((response) => {
          if (response?.data) {
            manualEditingForm.value = false;
            showSuccessMessage.value = true;
          }
        });
    };

    const closeSuccessModal = () => {
      showSuccessMessage.value = false;
      $emitter.emit('alert', {
        message:
          'Uw verzoek voor het wijzigen van uw gegevens zijn verzonden. Verwerking van de wijzigingen kunnen tot 5 werkdagen duren.'
      });
    };

    const closeModal = () => {
      manualEditingForm.value = false;
      showPopupErrorMessage.value = false;
      showSuccessMessage.value = false;
    };

    const checkRadioAddress = (value) => {
      editedUserPersonalDetails.value.gender = value;
    };

    const updatePersonalDetails = (key, event) => {
      editedUserPersonalDetails.value[key] = event.target.value;
    };

    const IsUserEmailAlreadyExists = async () => {
      return await axios
        .post('UserV2/IsUserEmailAlreadyExists', { email: editedUserPersonalDetails.value.email })
        .then((response) => {
          return response ? response.data : false;
        });
    };

    const saveUserData = async () => {
      await axios
        .post('UserV2/UpdateUser', {
          userId: userDetails.value.userId,
          titleId: editedUserPersonalDetails.value.gender,
          mobileNumber: editedUserPersonalDetails.value.mobileNumber
            ? editedUserPersonalDetails.value.mobileNumber
            : null,
          phoneNumberDialCode: editedUserPersonalDetails.value.phoneNumberDialCode
            ? editedUserPersonalDetails.value.phoneNumberDialCode
            : null,
          email: editedUserPersonalDetails.value.email.trim()
        })
        .then((response) => {
          if (response && response.data) {
            getUserDetails();
            successNotification('wijzigingen succesvol opgeslagen');
          } else {
            errorNotification('Er is een fout opgetreden, probeer het later nog eens');
          }
        });
    };

    const savePersonalDetails = async () => {
      const result = await validate();
      if (result.valid) {
        if (emailChanged.value) {
          let status = await IsUserEmailAlreadyExists();
          if (!status) {
            saveUserData();
          } else {
            showUserEmailExistAlert.value = true;
          }
          emailChanged.value = false;
        } else {
          if (!showUserEmailExistAlert.value) {
            saveUserData();
          }
        }
      }
    };

    const cancelPersonalDetailsSave = () => {
      setEditUserDetails();
      showPrescriberDetails.value = true;
      personalDetailsEdit.value = false;
      emailChanged.value = false;
    };

    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    };

    const editPersonalDetails = () => {
      showPrescriberDetails.value = false;
      organisationDetailsEdit.value = false;
      organisationDepartmentDetailsEdit.value = false;
      personalDetailsEdit.value = true;
      breadCrumbDetails.value = [
        { title: 'Account', cursor: true },
        { title: 'Persoonsgegevens wijzigen', cursor: false }
      ];
      scrollToTop();
    };
    const onTelephoneNumberSelect = (key, selectedCountry) => {
      editedUserPersonalDetails.value[key] = selectedCountry.dialCode;
    };
    const editOrganisationDetails = () => {
      axios.get('UserV2/GetAllUserInstitutions').then((resposne) => {
        if (resposne && resposne.data) {
          userInstitutions.value = resposne.data;
          showPrescriberDetails.value = false;
          organisationDetailsEdit.value = true;
          organisationDepartmentDetailsEdit.value = false;
          setOrganisationBreadcrumb();
        }
      });
    };

    const setOrganisationBreadcrumb = () => {
      breadCrumbDetails.value = [
        { title: 'Account', cursor: true },
        { title: 'Organisaties en afdelingen', cursor: false }
      ];
      scrollToTop();
    };

    const editOrganisationDepartments = (institutionId, institutionName, ref = null) => {
      axios.get('UserV2/GetDepartmentsByInstitutionId?institutionId=' + institutionId).then((resposne) => {
        if (resposne && resposne.data) {
          userInstitutionDepartments.value = resposne.data;
          currentOrgarnisationName.value = institutionName;
          organisationDepartmentDetailsEdit.value = true;

          showPrescriberDetails.value = false;
          organisationDetailsEdit.value = false;

          breadCrumbDetails.value = [
            { title: 'Account', cursor: true },
            { title: 'Organisaties en afdelingen', cursor: true, organisation: true },
            { title: institutionName, cursor: false }
          ];

          if (ref) {
            const targetSection = document.getElementById(ref);
            if (targetSection) {
              targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          } else {
            scrollToTop();
          }
        }
      });
    };

    const showDepartmentProxyDetails = (department) => {
      const url = `UserV2/GetAllProxyUsersByDepartment?ppwrecid=${userDetails.value.externalId}-${department.externalId}`;

      axios.get(url).then((response) => {
        if (response?.data) {
          proxyUserDetails.value = response.data;
          currentDepartment.value = department;
          departmentProxyDetails.value = true;

          showPrescriberDetails.value = false;
          organisationDetailsEdit.value = false;
          organisationDepartmentDetailsEdit.value = false;

          breadCrumbDetails.value = [
            { title: 'Account', cursor: true },
            { title: 'Organisaties en afdelingen', cursor: true, organisation: true },
            { title: currentOrgarnisationName.value, cursor: true, organisation: true, department: true },
            { title: currentDepartment.value.name, cursor: false }
          ];

          // Update proxy visibility based on the response data length
          const hasProxies = proxyUserDetails.value.length > 0;
          showProxy.value = hasProxies;
          addProxy.value = !hasProxies;
        }
      });
    };

    const setDepartmentBreadCrumb = () => {
      breadCrumbDetails.value = [
        { title: 'Account', cursor: true },
        { title: 'Organisaties en afdelingen', cursor: true, organisation: true },
        { title: currentOrgarnisationName, cursor: false }
      ];
    };

    const goBackToDepartment = () => {
      addProxy.value = false;
      departmentProxyDetails.value = false;
      organisationDepartmentDetailsEdit.value = true;
      setDepartmentBreadCrumb();
    };

    const setUpBreadCrumbs = (breadCrumb) => {
      if (!breadCrumb.cursor) {
        return;
      }
      showPrescriberDetails.value = false;
      personalDetailsEdit.value = false;
      organisationDetailsEdit.value = false;
      organisationDepartmentDetailsEdit.value = false;
      departmentProxyDetails.value = false;
      showChangePassword.value = false;
      showTwoFactorAuthentication.value = false;
      editTwoFactorAuthentication.value = false;
      showPrescriberTreatmentAreaDetails.value = false;
      if (breadCrumb.cursor) {
        if (breadCrumb.organisation) {
          if (breadCrumb.department) {
            organisationDepartmentDetailsEdit.value = true;
            setDepartmentBreadCrumb();
          } else {
            organisationDetailsEdit.value = true;
            if (userInstitutions.value.length === 0) {
              editOrganisationDetails();
            } else {
              setOrganisationBreadcrumb();
            }
          }
        } else {
          showPrescriberDetails.value = true;
        }
      }
    };

    const setChangePassword = () => {
      showChangePassword.value = true;
      showPrescriberDetails.value = false;
      breadCrumbDetails.value = [
        { title: 'Account', cursor: true },
        { title: 'Wachtwoord wijzigen', cursor: false }
      ];
    };

    const setTwoFactorAuthentication = () => {
      showTwoFactorAuthentication.value = true;
      showPrescriberDetails.value = false;
      breadCrumbDetails.value = [
        { title: 'Account', cursor: true },
        { title: '2FA instellen', cursor: false }
      ];
    };

    const TwoFactorAuthToggle = (status) => {
      editTwoFactorAuthentication.value = true;
      enableTwoFactorAuthencation.value = status;
    };

    const successNotification = (message) => {
      $emitter.emit('notification', {
        title: message
      });
    };

    const errorNotification = (message) => {
      $emitter.emit('notification', {
        title: message,
        success: false
      });
    };

    const editTreatmentAreaDetails = () => {
      showPrescriberTreatmentAreaDetails.value = true;
      showPrescriberDetails.value = false;
      breadCrumbDetails.value = [
        { title: 'Account', cursor: true },
        { title: 'Behandelgebieden wijzigen', cursor: false }
      ];
    };

    const savePrescriberTreatmentAreaDetails = (status) => {
      if (status) {
        showPrescriberDetails.value = true;
        showPrescriberTreatmentAreaDetails.value = false;
        getUserDetails();
        successNotification('wijzigingen succesvol opgeslagen');
      } else {
        errorNotification('Er is een fout opgetreden, probeer het later nog eens');
      }
    };
    const saveUserPreferenceEmail = async () => {
      let selectedEmails = [];
      disableEmailSelection.value = true;
      availableDepartments.value.forEach((dep) => {
        selectedEmails.push({ ppwRecId: dep.ppwrecId, email: dep.selected?.data });
      });
      await axios
        .post('UserV2/UpdateDepartmentPreferenceEmail', {
          preferenceEmails: selectedEmails
        })
        .then((response) => {
          if (response && response.data) {
            successNotification('wijzigingen succesvol opgeslagen');
          } else {
            errorNotification('Er is een fout opgetreden, probeer het later nog eens');
          }
        });
    };
    const saveUserCommunicationPreference = async (wantToReceiveEmails) => {
      let selectedEmails = [];
      disableEmailSelection.value = true;
      availableDepartments.value.forEach((dep) => {
        selectedEmails.push({ ppwRecId: dep.ppwrecId, email: dep.selected?.data });
      });
      if (wantToReceiveEmails != null) {
        await axios
          .post(
            'UserV2/UpdateUserCommunicationPreference?userId=' +
              currentUserIdentity.value.id +
              '&wantToReceiveEmails=' +
              wantToReceiveEmails
          )
          .then((response) => {
            if (response && response.data) {
              successNotification('wijzigingen succesvol opgeslagen');
            } else {
              errorNotification('Er is een fout opgetreden, probeer het later nog eens');
            }
          });
      }
    };
    const editUserPreferenceEmail = (status) => {
      setNotificationEmails.value = status;
      disableEmailSelection.value = !status;
    };

    const removeSpacesIfString = (value) => {
      if (typeof value === 'string' && value.includes(' ')) {
        return value.replace(/\s+/g, '');
      }
      return value;
    };

    const getComputedPhoneNumber = (number) => {
      if (!number) return null;
      const computedValue = number.startsWith('0') ? removeSpacesIfString(number.slice(1)) : number;
      editedUserPersonalDetails.value.mobileNumber = computedValue;
      return computedValue;
    };

    watch(
      () => editedUserPersonalDetails.value?.email,
      (newVal, oldVal) => {
        if (isInitialRun) {
          isInitialRun = false;
          return; // Skip watcher action on first run (page load)
        }
        if (newVal !== oldVal) {
          showUserEmailExistAlert.value = false;
          if (newVal !== userDetails.value.email) {
            emailChanged.value = true;
          } else {
            emailChanged.value = false;
          }
        }
      }
    );
    watch(setNotificationEmails, (newValue, oldValue) => {
      if (newValue == true) {
        disableEmailSelection.value = true;
      } else {
        disableEmailSelection.value = false;
      }
      if (initialLoad) {
        initialLoad = false;
        return;
      }
      if (newValue != oldValue) {
        saveUserCommunicationPreference(newValue);
      }
    });

    return {
      getComputedPhoneNumber,
      removeSpacesIfString,
      getUserDetails,
      userDetails,
      userInstitutions,
      userInstitutionDepartments,
      currentUserIdentity,
      showPrescriberDetails,
      personalDetailsEdit,
      savePersonalDetails,
      cancelPersonalDetailsSave,
      manualEditingForm,
      manualEditingFormDetails,
      showPopupErrorMessage,
      showSuccessMessage,
      saveEditDetailsInput,
      sendManualDetails,
      closeModal,
      closeSuccessModal,
      checkRadioAddress,
      updatePersonalDetails,
      showUserEmailExistAlert,
      editPersonalDetails,
      breadCrumbDetails,
      editOrganisationDetails,
      organisationDetailsEdit,
      organisationDepartmentDetailsEdit,
      currentOrgarnisationName,
      currentDepartment,
      editOrganisationDepartments,
      editedUserPersonalDetails,
      setEditUserDetails,
      departmentProxyDetails,
      showProxy,
      proxyUserDetails,
      addProxy,
      showDepartmentProxyDetails,
      goBackToDepartment,
      setOrganisationBreadcrumb,
      setDepartmentBreadCrumb,
      setUpBreadCrumbs,
      showChangePassword,
      show2FA,
      setChangePassword,
      showTwoFactorAuthentication,
      setTwoFactorAuthentication,
      TwoFactorAuthToggle,
      editTwoFactorAuthentication,
      enableTwoFactorAuthencation,
      successNotification,
      errorNotification,
      showPrescriberTreatmentAreaDetails,
      editTreatmentAreaDetails,
      savePrescriberTreatmentAreaDetails,
      availableDepartments,
      ppwRecId,
      setNotificationEmails,
      editUserPreferenceEmail,
      saveUserPreferenceEmail,
      disableEmailSelection,
      onTelephoneNumberSelect
    };
  }
};
</script>
<style lang="scss" scoped>
.prescriberDetailsEdit {
  color: $teal-darkest;
  :deep(.password-change-form) {
    margin-top: 0px !important;
  }
  :deep(.form-group-simple label) {
    color: $black;
    font-weight: 700;
    font-size: 16px;
  }
  .material-design-icon,
  :deep(.material-design-icon) {
    fill: $teal-darkest;
  }
  .notificationOn {
    color: $medium-green;
  }
  .notificationOff {
    color: #767676;
  }
  :deep(input) {
    color: $teal-darkest !important;
    height: 49px;
    border-color: #e5e7eb !important;
  }
}
</style>
